import React from 'react';

// FIN-14015: user-facing wording for the scoring config, shared by the scenario
// Alert Scoring tab and Settings > Scoring. Keep internal property names
// (lowMin, minFraction, SumCapped, ...) out of anything a user reads.

export const AGGREGATION_LABELS = {
  Max: 'Highest rule score',
  SumCapped: 'Sum of rule scores (up to 100)'
};

export const FIELD_LABELS = {
  minFraction: 'Minimum score share',
  capRatio: 'Full-score multiple'
};

export const TIPS = {
  bands: (
    <>
      Sets the score range (0–100) for each alert level. Enter the score where
      each level starts. Scores below where the lowest level starts are
      suppressed. Level names follow the Level Name Setting where configured.
    </>
  ),
  aggregation: (
    <>
      How the scores of several triggered rules combine into one alert score.
      <br />
      <b>{AGGREGATION_LABELS.Max}</b>: the alert takes the score of its
      highest-scoring rule.
      <br />
      <b>{AGGREGATION_LABELS.SumCapped}</b>: the rule scores are added together,
      up to a maximum of 100.
    </>
  ),
  deviation: (
    <>
      When on, a rule that only just passes its threshold gets part of its
      score, rising to the full score the further past the threshold the
      observed value goes. The score never goes above the rule score. When off,
      the full rule score is always used.
    </>
  ),
  minFraction: (
    <>
      Share of the rule score given when the observed value only just passes the
      threshold. For example, 0.7 gives 70% of the score. Must be more than 0
      and at most 1.
    </>
  ),
  capRatio: (
    <>
      How far past the threshold the observed value must go to earn the full
      rule score, as a multiple of the threshold. For example, 10 means ten
      times the threshold (or one tenth of it, for &quot;less than&quot; rules).
      Must be greater than 1.
    </>
  )
};

// Storage shape is {lowMin, lowMax, mediumMax}; users see where each band
// starts (Good start = lowMax + 1, High start = mediumMax + 1), so messages are
// phrased in those terms. Rules: 0 <= lowMin < lowMax < mediumMax < 100.
// `lowMin` is optional (absent = no check).
export const bandErrors = (
  { lowMin, lowMax, mediumMax },
  { low = 'Low', good = 'Good', high = 'High' } = {}
) => {
  const errors = [];
  if (typeof lowMin === 'number' && lowMin < 0)
    errors.push(`${low} cannot start below 0.`);
  if (typeof lowMin === 'number' && !(lowMin < lowMax))
    errors.push(`${low} must start at least 2 points below ${good}.`);
  if (!(lowMax >= 0)) errors.push(`${good} must start at 1 or above.`);
  if (!(lowMax < mediumMax)) errors.push(`${good} must start below ${high}.`);
  if (!(mediumMax < 100)) errors.push(`${high} must start at 100 or below.`);
  return errors;
};

export const deviationErrors = ({
  deviationEnabled,
  minFraction,
  capRatio
}) => {
  if (!deviationEnabled) return [];
  const errors = [];
  if (!(minFraction > 0 && minFraction <= 1))
    errors.push(
      `${FIELD_LABELS.minFraction} must be more than 0 and at most 1.`
    );
  if (!(capRatio > 1))
    errors.push(`${FIELD_LABELS.capRatio} must be greater than 1.`);
  return errors;
};
